import { GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Avatar, Card, Divider, Empty, Input, Tooltip, message } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useSupplierReconciliation } from '../../../../api/useSupplierReconciliation'
import { useSecurityContext } from '../../../Navigation/SecurityContext'
import { timeDisplay } from '../../shared/util/timeDisplay'

interface MatchComment {
  id: string
  username: string
  created_on: string
  text: string
}

interface MatchCommentsContainerProps {
  matchId: string
  comments: MatchComment[]
}

export function MatchCommentsContainer({ matchId, comments }: MatchCommentsContainerProps) {
  // Sort comments by timestamp (newest first)
  const sortedComments = [...comments].sort((a, b) => {
    return new Date(b.created_on).getTime() - new Date(a.created_on).getTime()
  })

  return <MatchCommentsPanel comments={sortedComments} matchId={matchId} />
}

function MatchCommentsPanel({
  comments,
  matchId,
}: {
  comments: MatchComment[]
  matchId: string
}) {
  const [newComment, setNewComment] = useState('')
  const { useAddCommentMutation, useRemoveCommentMutation } = useSupplierReconciliation()
  const addCommentMutation = useAddCommentMutation()
  const deleteCommentMutation = useRemoveCommentMutation()
  const { user } = useSecurityContext()

  const handleAddComment = async () => {
    if (!newComment.trim()) return

    try {
      await addCommentMutation.mutateAsync({
        match_id: matchId,
        msg: newComment.trim(),
      })
      setNewComment('')
      message.success('Comment added successfully')
    } catch (error) {
      console.error('Failed to add comment:', error)
      message.error('Failed to add comment')
    }
  }

  return (
    <Vertical style={{ height: '100%' }} gap={24}>
      <Vertical verticalCenter={comments.length === 0} className='p-3' flex={1} style={{ overflowY: 'auto' }} gap={12}>
        {comments.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No comments yet' style={{ margin: '24px 0' }} />
        ) : (
          comments.map((comment) => {
            const isCurrentUser = user?.username === comment.username
            const commentDate = dayjs(comment.created_on)
            const timeInfo = timeDisplay(comment.created_on)

            return (
              <Card
                key={comment.id}
                size='small'
                bordered={false}
                styles={{
                  body: {
                    padding: 12,
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--gray-200)',
                    borderLeft: isCurrentUser ? '5px solid var(--theme-color-2)' : '1px solid var(--gray-200)',
                    borderRadius: 8,
                  },
                }}
              >
                <Horizontal verticalCenter gap={6}>
                  <Avatar
                    style={{
                      background: isCurrentUser ? 'var(--theme-color-2)' : 'var(--dark-gray)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 24,
                    }}
                    size='small'
                  >
                    {comment.username.charAt(0).toUpperCase()}
                  </Avatar>

                  <Texto
                    style={{
                      flex: 1,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {comment.username}
                  </Texto>
                  <Tooltip title={commentDate.format('MMMM D, YYYY h:mm A')}>
                    <Texto style={{ flex: 0, whiteSpace: 'nowrap' }} category='label' appearance='hint'>
                      {timeInfo.display}
                    </Texto>
                  </Tooltip>
                </Horizontal>

                <Divider style={{ margin: '6px 0' }} />

                <Texto style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{comment.text}</Texto>
              </Card>
            )
          })
        )}
      </Vertical>
      <Vertical style={{ flex: 0, overflow: 'visible' }} bg='elevated'>
        <Card title='Add a Comment' size='small' bordered={false}>
          <Vertical gap={12}>
            <Input.TextArea
              placeholder='Type your comment here...'
              rows={3}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              style={{ borderRadius: '4px' }}
              count={{
                show: false,
                max: 300,
                exceedFormatter: (value) => value.slice(0, 300),
              }}
            />
            <Horizontal justifyContent='flex-end'>
              <GraviButton
                theme2
                buttonText='Add Comment'
                onClick={handleAddComment}
                disabled={!newComment.trim() || addCommentMutation.isPending}
                loading={addCommentMutation.isPending}
                icon={<MaterialIcon icon='add_comment' />}
              />
            </Horizontal>
          </Vertical>
        </Card>
      </Vertical>
    </Vertical>
  )
}
